import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  RequiredAsterisk,
  OptionalTag,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { CurrencySelect } from '@/components/ui/currency-select';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { useCurrency } from '@/hooks/useCurrency';
import { useAppSelector } from '@/features/hooks';
import type { Subscription } from '@/lib/types';
import { EmojiPicker } from '@/components/ui/emoji-picker';
import { TagInput } from '@/components/ui/tag-input';
import { TemplatePicker } from '@/components/templates/TemplatePicker';
import type { TransactionTemplate } from '@/lib/types';
import api from '@/services/api';

const subscriptionSchema = z.object({
  merchantName: z.string().min(1, 'Service name is required').max(100),
  amount: z.coerce.number().positive('Amount must be positive'),
  frequency: z.enum(['weekly', 'biweekly', 'monthly', 'quarterly', 'yearly', 'custom']),
  customIntervalDays: z.coerce.number().min(1).optional().or(z.literal('')).transform((v) => (v === '' ? undefined : v)),
  currency: z.string().length(3, 'Use a 3-letter currency code'),
  account: z.string().min(1, 'Account is required'),
  emoji: z.string().optional(),
  notes: z.string().max(500).optional().or(z.literal('')),
  startDate: z.string().optional().or(z.literal('')),
  endDate: z.string().optional().or(z.literal('')),
  indefinite: z.boolean().default(true),
  status: z.enum(['active', 'paused', 'cancelled', 'detected', 'expired']).default('active'),
  templateId: z.string().nullable().optional(),
  historyGenerationMode: z.enum(['auto', 'draft']).default('auto'),
  billingDayOfMonth: z.coerce.number().int().min(1).max(31).optional().or(z.literal('')).transform((v) => (v === '' ? undefined : v)),
  transactionDefaults: z.object({
    description: z.string().max(200).optional().or(z.literal('')),
    tags: z.array(z.string().max(30)).max(10).optional().default([]),
    merchant: z.string().max(100).optional().or(z.literal('')),
    notes: z.string().max(500).optional().or(z.literal('')),
    emoji: z.string().max(8).optional().or(z.literal('')),
    type: z.enum(['expense', 'income']).optional().or(z.literal('')),
    needsReview: z.boolean().default(false),
  }).optional().default({}),
});

export type SubscriptionFormValues = z.infer<typeof subscriptionSchema>;

interface SubscriptionFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (values: SubscriptionFormValues) => Promise<void>;
  subscription?: Subscription | null;
  isSubmitting?: boolean;
}

export function SubscriptionForm({ open, onOpenChange, onSubmit, subscription, isSubmitting }: SubscriptionFormProps) {
  const isEditing = !!subscription;
  const { currency: userCurrency } = useCurrency();
  const accounts = useAppSelector((state) => state.accounts.accounts);
  const [showTemplate, setShowTemplate] = useState(false);
  const [tagSuggestions, setTagSuggestions] = useState<string[]>([]);

  const form = useForm<SubscriptionFormValues>({
    resolver: zodResolver(subscriptionSchema),
    defaultValues: {
      merchantName: '',
      amount: 0,
      frequency: 'monthly',
      customIntervalDays: undefined,
      currency: userCurrency,
      account: '',
      emoji: '',
      notes: '',
      startDate: new Date().toISOString().split('T')[0],
      endDate: '',
      indefinite: true,
      status: 'active',
      templateId: null,
      historyGenerationMode: 'auto',
      billingDayOfMonth: undefined,
      transactionDefaults: {
        description: '',
        tags: [],
        merchant: '',
        notes: '',
        emoji: '',
        type: '' as '' | 'expense' | 'income',
        needsReview: false,
      },
    },
  });

  useEffect(() => {
    if (!open) return;
    api.get('/transactions/tags')
      .then((res) => {
        setTagSuggestions((res.data as { data?: string[] })?.data ?? []);
      })
      .catch(() => {});
  }, [open]);

  useEffect(() => {
    if (subscription) {
      const td = subscription.transactionDefaults;
      form.reset({
        merchantName: subscription.merchantName,
        amount: subscription.amount,
        frequency: subscription.frequency,
        customIntervalDays: subscription.customIntervalDays,
        currency: subscription.currency,
        account: (typeof subscription.account === 'object' && subscription.account !== null
          ? (subscription.account as { _id: string })._id
          : subscription.account) || '',
        emoji: subscription.emoji || '',
        notes: subscription.notes || '',
        startDate: subscription.startDate ? subscription.startDate.split('T')[0] : '',
        endDate: subscription.endDate ? subscription.endDate.split('T')[0] : '',
        indefinite: !subscription.endDate,
        status: subscription.status,
        templateId: subscription.templateId || null,
        historyGenerationMode: subscription.historyGenerationMode || 'auto',
        billingDayOfMonth: subscription.billingDayOfMonth ?? undefined,
        transactionDefaults: {
          description: td?.description || '',
          tags: td?.tags || [],
          merchant: td?.merchant || '',
          notes: td?.notes || '',
          emoji: td?.emoji || '',
          type: td?.type || ('' as '' | 'expense' | 'income'),
          needsReview: td?.needsReview || false,
        },
      });
      // Auto-expand template section if subscription has template data or linked template
      if (subscription.templateId || (td && (td.description || td.tags?.length || td.merchant || td.notes || td.needsReview))) {
        setShowTemplate(true);
      }
    } else {
      form.reset({
        merchantName: '',
        amount: 0,
        frequency: 'monthly',
        customIntervalDays: undefined,
        currency: userCurrency,
        account: '',
        emoji: '',
        notes: '',
        startDate: new Date().toISOString().split('T')[0],
        endDate: '',
        indefinite: true,
        status: 'active',
        templateId: null,
        historyGenerationMode: 'auto',
        billingDayOfMonth: undefined,
        transactionDefaults: {
          description: '',
          tags: [],
          merchant: '',
          notes: '',
          emoji: '',
          type: '' as '' | 'expense' | 'income',
          needsReview: false,
        },
      });
      setShowTemplate(false);
    }
  }, [subscription, form, open, userCurrency]);

  useEffect(() => {
    if (!subscription && accounts.length === 1 && !form.getValues('account')) {
      form.setValue('account', accounts[0].id);
    }
  }, [accounts, subscription, form]);

  const handleSubmit = async (values: SubscriptionFormValues) => {
    await onSubmit(values);
    form.reset();
  };

  const watchFrequency = form.watch('frequency');
  const watchIndefinite = form.watch('indefinite');

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{isEditing ? 'Edit Subscription' : 'Add Subscription'}</SheetTitle>
          <SheetDescription>
            {isEditing ? 'Update subscription details.' : 'Track a new recurring subscription.'}
          </SheetDescription>
        </SheetHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="emoji"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Emoji<OptionalTag /></FormLabel>
                  <FormControl>
                    <EmojiPicker value={field.value} onChange={field.onChange} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="merchantName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Service Name<RequiredAsterisk /></FormLabel>
                  <FormControl>
                    <Input placeholder="Netflix, Spotify, etc." {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid gap-3 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="amount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Amount<RequiredAsterisk /></FormLabel>
                    <FormControl>
                      <Input type="number" step="0.01" placeholder="9.99" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="currency"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Currency<RequiredAsterisk /></FormLabel>
                    <FormControl>
                      <CurrencySelect
                        value={field.value || ''}
                        onChange={field.onChange}
                        required
                        testId="currency-select-subscription"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="account"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Account<RequiredAsterisk /></FormLabel>
                  <Select onValueChange={field.onChange} value={field.value} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger data-testid="subscription-account-select">
                        <SelectValue placeholder="Select account" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {accounts.map((acc) => (
                        <SelectItem key={acc.id} value={acc.id}>
                          {acc.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="frequency"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Billing Cycle<RequiredAsterisk /></FormLabel>
                  <Select onValueChange={field.onChange} value={field.value} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="biweekly">Biweekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="quarterly">Quarterly</SelectItem>
                      <SelectItem value="yearly">Yearly</SelectItem>
                      <SelectItem value="custom">Custom</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {watchFrequency === 'custom' && (
              <FormField
                control={form.control}
                name="customIntervalDays"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Custom Interval (days)<RequiredAsterisk /></FormLabel>
                    <FormControl>
                      <Input type="number" min="1" placeholder="e.g. 14" {...field} value={field.value ?? ''} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="billingDayOfMonth"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Billing Day of Month<OptionalTag /></FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min={1}
                      max={31}
                      placeholder="Auto (from start date)"
                      data-testid="billing-day-input"
                      {...field}
                      value={field.value ?? ''}
                      onChange={e => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                    />
                  </FormControl>
                  <FormDescription>Day of month for recurring billing (1–31). Leave blank to auto-set.</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {isEditing && (
              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Status</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="paused">Paused</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                        <SelectItem value="expired">Expired</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="startDate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Start Date<OptionalTag /></FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="indefinite"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center gap-2 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={(checked) => {
                        field.onChange(!!checked);
                        if (checked) form.setValue('endDate', '');
                      }}
                    />
                  </FormControl>
                  <FormLabel className="text-sm font-normal">Indefinite (no end date)</FormLabel>
                </FormItem>
              )}
            />

            {!watchIndefinite && (
              <FormField
                control={form.control}
                name="endDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>End Date<OptionalTag /></FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes<OptionalTag /></FormLabel>
                  <FormControl>
                    <Textarea placeholder="Optional notes about this subscription..." rows={3} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Transaction Template Section */}
            <div className="space-y-3">
              <button
                type="button"
                className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                onClick={() => setShowTemplate(!showTemplate)}
                data-testid="toggle-transaction-template"
              >
                {showTemplate ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                Transaction Template
                <span className="text-xs text-muted-foreground">(optional)</span>
              </button>

              {showTemplate && (
                <div className="space-y-3 rounded-lg border p-3" data-testid="transaction-template-section">
                  <FormField
                    control={form.control}
                    name="templateId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Link Template<OptionalTag /></FormLabel>
                        <FormControl>
                          <TemplatePicker
                            value={field.value ?? null}
                            onChange={(templateId: string | null, template?: TransactionTemplate) => {
                              field.onChange(templateId);
                              if (template) {
                                const td = form.getValues('transactionDefaults');
                                form.setValue('transactionDefaults', {
                                  description: template.description || td?.description || '',
                                  tags: template.tags?.length ? template.tags : td?.tags || [],
                                  merchant: template.merchant || td?.merchant || '',
                                  notes: template.notes || td?.notes || '',
                                  emoji: template.emoji || td?.emoji || '',
                                  type: template.type || td?.type || ('' as '' | 'expense' | 'income'),
                                  needsReview: template.needsReview ?? td?.needsReview ?? false,
                                });
                              }
                            }}
                          />
                        </FormControl>
                        {field.value && (
                          <p className="text-xs text-muted-foreground">
                            Fields below are overridden by the linked template during billing.
                          </p>
                        )}
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="historyGenerationMode"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Generation Mode<OptionalTag /></FormLabel>
                        <Select onValueChange={field.onChange} value={field.value} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select mode" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="auto">Auto — confirm immediately</SelectItem>
                            <SelectItem value="draft">Draft — preview before confirm</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="transactionDefaults.description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Description<OptionalTag /></FormLabel>
                        <FormControl>
                          <Input placeholder="Custom description for generated transactions" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="transactionDefaults.emoji"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Emoji<OptionalTag /></FormLabel>
                        <FormControl>
                          <EmojiPicker value={field.value || ''} onChange={field.onChange} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="transactionDefaults.tags"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Tags<OptionalTag /></FormLabel>
                        <TagInput
                          value={field.value as string[] ?? []}
                          onChange={field.onChange}
                          suggestions={tagSuggestions}
                          placeholder="Add tags..."
                          maxTags={10}
                        />
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="transactionDefaults.merchant"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Merchant Override<OptionalTag /></FormLabel>
                        <FormControl>
                          <Input placeholder="Merchant name override" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="transactionDefaults.type"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Transaction Type<OptionalTag /></FormLabel>
                        <Select onValueChange={field.onChange} value={field.value || ''} defaultValue={field.value || ''}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Default (Expense)" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="expense">Expense</SelectItem>
                            <SelectItem value="income">Income</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="transactionDefaults.notes"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Template Notes<OptionalTag /></FormLabel>
                        <FormControl>
                          <Textarea placeholder="Notes for generated transactions..." rows={2} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="transactionDefaults.needsReview"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center gap-2 space-y-0">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={(checked) => field.onChange(!!checked)}
                          />
                        </FormControl>
                        <FormLabel className="text-sm font-normal">Mark generated transactions for review</FormLabel>
                      </FormItem>
                    )}
                  />
                </div>
              )}
            </div>

            <SheetFooter>
              <Button type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? 'Saving...' : isEditing ? 'Save Changes' : 'Add Subscription'}
              </Button>
            </SheetFooter>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
}
